import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getMenus, addMenu } from "helpers/backend_helper";
import { Loader2 } from "lucide-react";

interface MenuFormProps {
    onSave: (data: any) => void;
    onHide: () => void;
    data: any;
}

interface MenuFormValues {
    id?: number;
    name: string;
    url: string;
    icon: string;
    parentId?: number;
    isActive: boolean;
}

const MenuForm = ({ onSave, onHide, data }: MenuFormProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [menus, setMenus] = useState<any[]>([]);

    useEffect(() => {
        fetchMenus();
    }, []);

    const fetchMenus = async () => {
        try {
            const response = await getMenus();
            setMenus(response.data || []);
        } catch (error) {
            console.error("Error fetching menus:", error);
        }
    };

    const validation = useFormik<MenuFormValues>({
        enableReinitialize: true,
        initialValues: {
            id: data?.id || 0,
            name: data?.name || "",
            url: data?.url || "",
            icon: data?.icon || "noimage.jpg",
            parentId: data?.parentId || 0,
            isActive: data?.isActive ?? true
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
            url: Yup.string().required("URL is required"),
            parentId: Yup.string(),
            isActive: Yup.boolean()
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (values.id == 0) {
                    delete values.id;
                }
                if (values.parentId == 0) {
                    delete values.parentId;
                }
                

                const response = await addMenu(values) as any;
                if (response?.success) {
                    onSave(response.data);
                }
            } catch (error) {
                console.error("Error saving menu:", error);
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h5 className="text-16">{data?.id ? "Edit Menu" : "Add Menu"}</h5>
                <button
                    type="button"
                    className="text-slate-400 hover:text-slate-500"
                    onClick={onHide}
                >
                    <span className="sr-only">Close</span>
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <form onSubmit={validation.handleSubmit}>
                <div className="grid grid-cols-1 gap-4">
                    <div>
                        <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                            Menu Name
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                            placeholder="Enter menu name"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.name}
                        />
                        {validation.touched.name && validation.errors.name && (
                            <div className="mt-1 text-sm text-red-500">
                                {validation.errors.name.toString()}
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="url" className="inline-block mb-2 text-base font-medium">
                            URL
                        </label>
                        <input
                            type="text"
                            id="url"
                            name="url"
                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                            placeholder="Enter URL"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.url}
                        />
                        {validation.touched.url && validation.errors.url && (
                            <div className="mt-1 text-sm text-red-500">
                                {validation.errors.url.toString()}
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="parentId" className="inline-block mb-2 text-base font-medium">
                            Parent Menu
                        </label>
                        <select
                            id="parentId"
                            name="parentId"
                            className="form-select border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.parentId}
                        >
                            <option value="">None</option>
                            {menus.map((menu) => (
                                <option key={menu.id} value={menu.id}>
                                    {menu.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                            Status
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="isActive"
                                name="isActive"
                                className="size-4 border rounded-sm appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                                onChange={validation.handleChange}
                                checked={validation.values.isActive}
                            />
                            <label htmlFor="isActive" className="inline-block text-base font-medium align-middle cursor-pointer">
                                Active
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button
                        type="button"
                        className="text-slate-500 btn bg-slate-200 border-slate-200 hover:text-white hover:bg-slate-300 hover:border-slate-300 focus:text-white focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:text-zink-200 dark:border-zink-600 dark:hover:border-zink-500 dark:focus:border-zink-500 dark:focus:ring-zink-200/50 dark:active:bg-zink-500 dark:active:border-zink-500"
                        onClick={onHide}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                                {data?.id ? "Updating..." : "Saving..."}
                            </>
                        ) : (
                            data?.id ? "Update" : "Save"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MenuForm; 